// ** React Imports
import { ReactNode, useEffect } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** Types
import type { AppAbility } from 'src/configs/acl'

// ** Context Imports
import { AbilityContext } from 'src/layouts/components/acl/Can'

// ** Config Import
import { buildAbilityFor } from 'src/configs/acl'

// ** Component Import
import NotAuthorized from 'src/pages/401'
import Spinner from 'src/@core/components/spinner'
import BlankLayout from 'src/@core/layouts/BlankLayout'

// ** Hooks
import { useAuth } from 'src/hooks/useAuth'

// ** Util Import
import getHomeRoute from 'src/layouts/components/acl/getHomeRoute'
import { ACLObj } from '@/src/types/apps/permissionTypes'
import { ROUTES } from '@/src/utils/routes'
import { getUserRestaurants } from '@/src/utils/restaurantAccessUtils'

// ** Iframe Utils
import { isIframeRequest, getStoredIframeParams } from '@/src/utils/iframeUtils'

interface AclGuardProps {
  children: ReactNode
  authGuard?: boolean
  guestGuard?: boolean
  aclAbilities: ACLObj
}

const AclGuard = (props: AclGuardProps) => {
  // ** Props
  const { aclAbilities, children, guestGuard = false, authGuard = true } = props

  // ** Hooks
  const auth = useAuth()
  const router = useRouter()

  // ** Vars
  let ability: AppAbility

  useEffect(() => {
    // Don't redirect to home route if this is an iframe request or has stored iframe params
    const isIframe = isIframeRequest()
    const hasStoredIframeParams = getStoredIframeParams() !== null

    if (auth.user && auth.user.role && !guestGuard && router.route === '/' && !isIframe && !hasStoredIframeParams) {
      const homeRoute = getHomeRoute(auth.user.role.name)
      void router.replace(homeRoute)
    }
  }, [auth.user, guestGuard, router])

  // User is logged in, build ability for the user based on his role
  if (auth.user && !ability) {
    ability = buildAbilityFor(auth.user.permissions)
    if (router.route === '/') {
      return <Spinner />
    }
  }

  // If guest guard or no guard is true or any error page
  if (guestGuard || router.route === ROUTES.NOT_FOUND || router.route === ROUTES.SERVER_ERROR || !authGuard) {
    // If user is logged in and his ability is built
    if (auth.user && ability) {
      return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
    } else {
      // If user is not logged in (render pages like login, register etc..)
      return <>{children}</>
    }
  }

  // Check the access of current user and render pages
  // Special handling for restaurant scope users: allow access if they have restaurants assigned
  // even if they don't have explicit read:restaurant permission
  const hasStandardPermission =
    ability && auth.user && aclAbilities.subject !== 'none' && ability.can(aclAbilities.action, aclAbilities.subject)

  // Allow restaurant scope users to access restaurant pages if they have restaurants assigned
  const userRestaurants = auth.user ? getUserRestaurants(auth.user) : []
  const isRestaurantScopeWithRestaurants =
    auth.user?.role?.scope === 'restaurant' &&
    aclAbilities.subject === 'restaurant' &&
    aclAbilities.action === 'read' &&
    userRestaurants.length > 0

  // Debug logging (only in development)
  if (process.env.NODE_ENV === 'development' && auth.user && aclAbilities.subject === 'restaurant') {
    // eslint-disable-next-line no-console
    console.log('[AclGuard] Restaurant access check:', {
      route: router.route,
      userScope: auth.user?.role?.scope,
      subject: aclAbilities.subject,
      action: aclAbilities.action,
      hasStandardPermission,
      isRestaurantScopeWithRestaurants,
      userRestaurantsCount: userRestaurants.length,
      userRestaurants: userRestaurants.slice(0, 3) // Show first 3 for debugging
    })
  }

  if (hasStandardPermission || isRestaurantScopeWithRestaurants) {
    if (router.route === '/') {
      return <Spinner />
    }

    // Ensure ability is defined before using it
    // If user exists but ability wasn't built yet, build it now
    if (!ability && auth.user) {
      ability = buildAbilityFor(auth.user.permissions)
    }

    // If ability is still not defined, show spinner
    if (!ability) {
      return <Spinner />
    }

    return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
  }

  // Render Not Authorized component if the current user has limited access
  return (
    <BlankLayout>
      <NotAuthorized />
    </BlankLayout>
  )
}

export default AclGuard
